(function () {
  const CHUNK_SIZE = 250;
  const INDEX_THROTTLE_MS = 300; // delay between background index page fetches
  const RESULTS_BATCH_SIZE = 24; // results rendered per scroll batch
  const RESULTS_ROOT_MARGIN = '600px';

  function refreshStockDots() {
    if (window.BillyStockDot && typeof window.BillyStockDot.refresh === 'function') {
      window.BillyStockDot.refresh();
    }
  }

  function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  class CollectionSearch {
    constructor(root) {
      this.root = root;
      this.collectionUrl = root.dataset.collectionUrl;
      this.input = root.querySelector('[data-collection-search-input]');
      if (!this.input) {
        console.error('Collection search: missing required markup', root);
        return;
      }
      this.bindGridNodes();
      if (!this.browseGrid || !this.resultsContainer || !this.statusEl) {
        console.error('Collection search: missing required markup', root);
        return;
      }

      // Search index state (built once, shared across queries)
      this.index = [];
      this.indexedPages = 0;
      this.totalPages = null;
      this.indexingStarted = false;

      // Rendered-results state (reset on every new query)
      this.currentMatches = [];
      this.renderedCount = 0;
      this.resultsObserver = null;
      this.resultsSentinel = null;

      this.input.addEventListener('input', this.onInput.bind(this));
      // Start building the index as soon as the shopper shows intent to search,
      // instead of on every collection page load regardless of usage.
      this.input.addEventListener('focus', this.startIndexing.bind(this), { once: true });
    }

    // (Re)locate the grid/results/status nodes by id/attribute rather than
    // caching them permanently: facets.js (vendor/price/etc. filtering)
    // replaces #ProductGridContainer's innerHTML wholesale on every filter
    // change, so these elements are swapped out for fresh, unbound copies.
    bindGridNodes() {
      this.browseGrid = document.getElementById(this.root.dataset.gridId);
      this.resultsContainer = this.root.querySelector('[data-collection-search-results]');
      this.statusEl = this.root.querySelector('[data-collection-search-status]');
    }

    // Called after a facet/filter change swaps the product grid: the
    // previously-built index reflects the old (unfiltered or differently
    // filtered) product set, so it must be rebuilt against the new one.
    reset() {
      const wasSearching = this.indexingStarted || this.isSearchActive();
      this.bindGridNodes();
      this.teardownResultsObserver();
      this.index = [];
      this.indexedPages = 0;
      this.totalPages = null;
      this.indexingStarted = false;
      this.currentMatches = [];
      this.renderedCount = 0;
      if (this.isSearchActive()) {
        this.showResults();
        this.renderResults(this.input.value.trim());
      } else {
        this.showBrowse();
      }
      if (wasSearching) this.startIndexing();
    }

    async fetchIndexPage(page) {
      // Reflect the collection's current facet filters / sort (vendor,
      // price, availability, sort_by, ...) so the search index only covers
      // the same pre-filtered product set the shopper is already looking at.
      const activeFilters = window.location.search ? window.location.search.slice(1) : '';
      const url = `${this.collectionUrl}?view=search-index&page=${page}${activeFilters ? '&' + activeFilters : ''}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error('search index fetch failed: ' + res.status);
      return JSON.parse(await res.text());
    }

    startIndexing() {
      if (this.indexingStarted) return;
      this.indexingStarted = true;
      this.runIndexer();
    }

    // Single background indexing loop (no concurrent competing loop, so no
    // risk of fetching the same page twice / duplicating index entries).
    // Runs to completion regardless of whether the shopper is actively
    // typing, throttled so a huge collection never bursts many requests
    // at once.
    async runIndexer() {
      let page = 1;
      while (!this.totalPages || page <= this.totalPages) {
        try {
          const items = await this.fetchIndexPage(page);
          if (!items.length) {
            this.totalPages = page - 1;
            break;
          }
          this.index.push(...items);
          this.indexedPages = page;
          if (this.isSearchActive()) this.renderResults(this.input.value.trim());
          if (items.length < CHUNK_SIZE) {
            this.totalPages = page;
            break;
          }
        } catch (e) {
          console.error('Collection search index error', e);
          break;
        }
        page++;
        if (!this.totalPages || page <= this.totalPages) {
          await sleep(INDEX_THROTTLE_MS);
        }
      }
    }

    isSearchActive() {
      return this.input.value.trim().length > 0;
    }

    onInput() {
      clearTimeout(this._debounce);
      this._debounce = setTimeout(() => {
        const query = this.input.value.trim();
        if (!query) {
          this.showBrowse();
          return;
        }
        this.showResults();
        this.renderResults(query);
      }, 150);
    }

    renderResults(query) {
      const q = query.toLowerCase();
      this.currentMatches = this.index.filter((p) => p.title.toLowerCase().includes(q));
      this.renderedCount = 0;
      this.teardownResultsObserver();
      this.resultsContainer.innerHTML = '';
      this.appendNextResultsBatch();
      this.updateStatus();
    }

    appendNextResultsBatch() {
      const next = this.currentMatches.slice(this.renderedCount, this.renderedCount + RESULTS_BATCH_SIZE);
      if (next.length) {
        this.resultsContainer.insertAdjacentHTML('beforeend', next.map((p) => this.renderCard(p)).join(''));
        this.renderedCount += next.length;
        refreshStockDots();
      }
      if (this.renderedCount < this.currentMatches.length) {
        this.setupResultsSentinel();
      } else {
        this.teardownResultsObserver();
      }
    }

    setupResultsSentinel() {
      if (!this.resultsSentinel) {
        this.resultsSentinel = document.createElement('div');
        this.resultsSentinel.setAttribute('data-search-results-sentinel', '');
        this.resultsSentinel.style.height = '1px';
      }
      this.resultsContainer.appendChild(this.resultsSentinel);
      if (!this.resultsObserver) {
        this.resultsObserver = new IntersectionObserver((entries) => {
          if (entries.some((e) => e.isIntersecting)) this.appendNextResultsBatch();
        }, { rootMargin: RESULTS_ROOT_MARGIN });
      }
      this.resultsObserver.disconnect();
      this.resultsObserver.observe(this.resultsSentinel);
    }

    teardownResultsObserver() {
      if (this.resultsObserver) this.resultsObserver.disconnect();
      if (this.resultsSentinel && this.resultsSentinel.parentNode) {
        this.resultsSentinel.parentNode.removeChild(this.resultsSentinel);
      }
    }

    updateStatus() {
      const total = this.currentMatches.length;
      const trulyComplete = this.totalPages !== null && this.indexedPages >= this.totalPages;
      let statusText;
      if (trulyComplete) {
        statusText = `${total} résultat${total !== 1 ? 's' : ''}`;
      } else {
        statusText = `${total} résultat${total !== 1 ? 's' : ''} (recherche en cours sur le reste de la collection…)`;
      }
      this.statusEl.textContent = statusText;
      this.statusEl.hidden = false;
    }

    renderCard(p) {
      let badge = '';
      if (!p.available) {
        badge =
          '<span class="badge sale-badge--rounded sale-badge--topleft soldout-badge">' +
          (window.collectionSearchStrings?.soldOut || 'Sold out') +
          '</span>';
      } else if (p.on_sale) {
        let rounded = Math.floor((p.discount_percent + 2.5) / 5) * 5;
        if (rounded < 5) rounded = 5;
        badge = `<span class="badge sale-badge--rounded sale-badge--topleft">-${rounded}% OFF</span>`;
      }
      const priceHtml = p.on_sale
        ? `<s class="price-item price-item--regular">${p.compare_at_price}</s> <span class="price-item price-item--sale price-item--last">${p.price}</span>`
        : `<span class="price-item price-item--regular">${p.price}</span>`;
      return `<li class="grid__item"><div class="card-wrapper product-card-wrapper underline-links-hover"><div class="card card--card" style="position:relative;">${badge}<div class="card__inner ratio" style="--ratio-percent:100%;"><div class="card__media"><a href="${p.url}" class="media media--transparent" style="display:block;position:absolute;inset:0;height:100%;width:100%;overflow:hidden;"><img src="${this.escapeAttr(p.image)}" alt="${this.escapeAttr(p.title)}" loading="lazy" class="motion-reduce" style="position:absolute;top:0;left:0;height:100%;width:100%;object-fit:cover;"></a></div></div><div class="card__content"><div class="card__information"><h3 class="card__heading h5"><a href="${p.url}" class="full-unstyled-link">${this.escapeHtml(p.title)}</a></h3><div class="price">${priceHtml}</div></div></div></div></div></li>`;
    }

    escapeHtml(str) {
      const d = document.createElement('div');
      d.textContent = str;
      return d.innerHTML;
    }

    escapeAttr(str) {
      return this.escapeHtml(str).replace(/"/g, '&quot;');
    }

    showResults() {
      this.browseGrid.hidden = true;
      this.resultsContainer.hidden = false;
    }

    showBrowse() {
      this.resultsContainer.hidden = true;
      this.browseGrid.hidden = false;
      this.statusEl.hidden = true;
    }
  }

  class CollectionInfiniteScroll {
    constructor(root) {
      this.root = root;
      this.grid = document.getElementById(root.dataset.gridId);
      this.sentinel = root.querySelector('[data-infinite-scroll-sentinel]');
      this.sectionId = root.dataset.sectionId;
      this.nextUrl = this.sentinel ? this.sentinel.dataset.nextUrl || null : null;
      this.loading = false;
      if (!this.grid || !this.grid.id || !this.sentinel || !this.nextUrl || !this.sectionId) return;
      this.observer = new IntersectionObserver(this.onIntersect.bind(this), { rootMargin: '600px' });
      this.observer.observe(this.sentinel);
    }

    async onIntersect(entries) {
      if (this.loading || !this.nextUrl || !entries.some((e) => e.isIntersecting)) return;
      this.loading = true;
      try {
        const url = new URL(this.nextUrl, window.location.origin);
        url.searchParams.set('section_id', this.sectionId);
        const res = await fetch(url.toString());
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        doc.querySelectorAll('#' + this.grid.id + ' .grid__item').forEach((item) => this.grid.appendChild(item));
        const newSentinel = doc.querySelector('[data-infinite-scroll-sentinel]');
        this.nextUrl = newSentinel ? newSentinel.dataset.nextUrl || null : null;
        if (!this.nextUrl) this.observer.disconnect();
        refreshStockDots();
      } catch (e) {
        console.error('Infinite scroll error', e);
        this.observer.disconnect();
      } finally {
        this.loading = false;
      }
    }
  }

  document.querySelectorAll('[data-collection-search]').forEach((root) => {
    const search = new CollectionSearch(root);
    // #ProductGridContainer: carries data-collection-infinite-scroll AND is
    // the exact node facets.js replaces the innerHTML of on every filter
    // change (vendor, price, availability, sort...). Watching it lets both
    // the search index and the infinite-scroll observer reconnect to the
    // fresh grid/sentinel instead of going stale.
    const gridContainer = root.querySelector('[data-collection-infinite-scroll]');
    if (!gridContainer) return;
    new CollectionInfiniteScroll(gridContainer);
    const observer = new MutationObserver(() => {
      if (search.bindGridNodes) search.reset();
      new CollectionInfiniteScroll(gridContainer);
    });
    observer.observe(gridContainer, { childList: true });
  });
})();
